import React, { useCallback, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import AntTooltip, { TooltipProps as AntTooltipProps } from 'antd/lib/tooltip';

import { isMobile } from '@utils/window';

import { Text, Message, Icon } from '@atoms';

import classes from './tooltip.module.scss';

export type TooltipProps = {
  titleMessage: Message;
  hideArrow?: boolean;
  type?: 'green' | 'dark';
  size?: 'mid' | 'big';
  borderRadius?: 'big';
  className?: string;
  innerWrapperClassName?: string;
  withExitButton?: boolean;
  onVisibilityChange?: () => void;
} & Omit<AntTooltipProps, 'overlay'>;
type Props = TooltipProps;

type TooltipContentProps = {
  message: Message;
  className?: string;
  withExitButton?: boolean;
  onExitButtonClick?: () => void;
};

const useDidMountEffect = (func: () => void, deps: any) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
  }, deps);
};

const TooltipContent = ({
  message,
  withExitButton,
  onExitButtonClick,
  className,
}: TooltipContentProps) => {
  return (
    <div className={clsx(classes.tooltipContentWrapper, className)}>
      <Text className={classes.text} message={message} />
      {withExitButton && (
        <Icon
          onClick={onExitButtonClick}
          className={classes.exitIcon}
          icon="close"
        />
      )}
    </div>
  );
};

export const Tooltip = ({
  titleMessage,
  hideArrow,
  type,
  size,
  className,
  borderRadius,
  withExitButton,
  innerWrapperClassName,
  onVisibilityChange,
  ...props
}: Props) => {
  const [isVisible, setIsVisible] = useState(false);

  useDidMountEffect(() => {
    if (onVisibilityChange && isVisible === true) onVisibilityChange();
  }, [isVisible]);

  if (isMobile()) {
    props.trigger = 'click';
  }

  const closeTooltip = useCallback(() => setIsVisible(false), [setIsVisible]);

  return (
    <AntTooltip
      onVisibleChange={(v) => setIsVisible(v)}
      visible={isVisible}
      title={
        <TooltipContent
          className={innerWrapperClassName}
          message={titleMessage}
          withExitButton={withExitButton}
          onExitButtonClick={closeTooltip}
        />
      }
      overlayClassName={clsx(
        classes.tooltip,
        hideArrow && classes.tooltipNoArrow,
        borderRadius === 'big' && classes.borderRadiusBig,
        type === 'green' && classes.tooltipGreen,
        type === 'dark' && classes.tooltipDark,
        size === 'mid' && classes.tooltipMid,
        size === 'big' && classes.tooltipBig,
        className,
      )}
      {...props}
    />
  );
};
